import * as R from 'ramda'
import {
  GetMessagesViewCount,
  Message,
  reqGetMessagesViewCount,
  sendViewMessages,
} from '@weblite/ts-action/message'
import { bufferFunction } from '$func/buffer-function'
import { ViewCountableMessage } from '$logic/message/types'
import { request, send } from '$setup/ws'
import { openedRoomMessagesStorage } from '$logic/opened-room/opened-room-messages.storage'
import { messagesStorage } from '$logic/message/storage'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import { MESSAGE_VIEW_COUNT_IS_ENABLED } from '$config/env'
import {
  MIN_TIME_FROM_LAST_UPDATE_TO_UPDATE_VIEW_COUNT,
  MIN_TIME_FROM_SENDING_MESSAGE_TO_UPDATE_VIEW_COUNT,
} from '$logic/conf'

function getMessageReference(message: Message) {
  if (
    message.forwardedFrom?.type === 'CHANNEL' &&
    message.sender.type !== 'CHANNEL' &&
    message.forwardedFrom.iid
  )
    return { roomId: message.forwardedFrom.id, iid: message.forwardedFrom.iid }
  return { roomId: message.roomId, iid: message.iid }
}

function shouldFetchMessageViewCount(message: ViewCountableMessage) {
  // TODO: Should consider other factors
  const messageDate = message.date
  const now = dateWithOffset()
  const timePassedFromSendingMessage = now - messageDate
  const timePassedFromLastViewUpdate =
    now - new Date(message.lastViewUpdate || 0).getTime()

  return (
    timePassedFromLastViewUpdate >
      MIN_TIME_FROM_LAST_UPDATE_TO_UPDATE_VIEW_COUNT &&
    timePassedFromSendingMessage >
      MIN_TIME_FROM_SENDING_MESSAGE_TO_UPDATE_VIEW_COUNT
  )
}

const updateViewCount = bufferFunction((inputs: ViewCountableMessage[]) => {
  if (!MESSAGE_VIEW_COUNT_IS_ENABLED) return
  const messages = inputs.filter(shouldFetchMessageViewCount).map(message => ({
    ...message,
    reference: getMessageReference(message as Message),
  }))

  const grouped = R.groupBy(message => message.reference.roomId, messages)

  R.toPairs(grouped).map(async ([roomId, groupedMessage]) => {
    const res = await request<GetMessagesViewCount>(
      reqGetMessagesViewCount({
        roomId,
        iids: groupedMessage.map(message => message.reference.iid),
      }),
    )
    res.payload?.viewCounts?.forEach((view, index) => {
      if (Number.isInteger(view)) {
        const message = groupedMessage[index]
        if (
          openedRoomMessagesStorage.get(message.iid)?.roomId === message.roomId
        )
          openedRoomMessagesStorage.editViewCount(message.iid, view || 1)

        messagesStorage.updateOne([message.roomId, message.iid], {
          $set: {
            viewCount: view || 1,
            lastViewUpdate: new Date(dateWithOffset()),
          },
        })
      }
    })
  })
}, 1000)

const sendViewMessage = bufferFunction(
  (inputs: { roomId: string; iid: number }[]) => {
    const grouped = R.groupBy(R.prop('roomId'), inputs)
    R.toPairs(grouped).forEach(([roomId, values]) => {
      send(sendViewMessages({ roomId, iids: values.map(R.prop('iid')) }))
    })
  },
  1000,
)

export async function handleViewCount(message: ViewCountableMessage) {
  if (!MESSAGE_VIEW_COUNT_IS_ENABLED || message.type === 'EVENT') return
  if (
    !(
      message.sender?.type === 'CHANNEL' ||
      message.forwardedFrom?.type === 'CHANNEL'
    )
  )
    return

  updateViewCount(message)

  if (message.isViewed) return

  sendViewMessage(getMessageReference(message as Message))

  await messagesStorage.updateOne([message.roomId, message.iid], {
    $set: { isViewed: true },
  })
}
